import { useEffect, useRef, useState } from 'react';

const ESC_POS_PULSE_COMMAND = '\x1B\x70\x00\x19\xFA';

const useTray = () => {
  const initialized = useRef(false);
  const connecting = useRef(false);
  const [connected, setConnected] = useState(false);
  const deviceRef = useRef(null);

const connectToDevice = async (usbDevice) => {
  try {
    if (!usbDevice.opened) {
      await usbDevice.open();
    }

    if (usbDevice.configuration === null) {
      await usbDevice.selectConfiguration(1);
    }

    const outInterface = usbDevice.configuration.interfaces.find(i =>
      i.alternate.endpoints.some(e => e.direction === 'out')
    );

    if (!outInterface) throw new Error('No OUT endpoint found');

    const alreadyClaimed = usbDevice.configuration.interfaces
      .some(i => i.claimed && i.interfaceNumber === outInterface.interfaceNumber);

    if (!alreadyClaimed) {
      await usbDevice.claimInterface(outInterface.interfaceNumber);
    }

    deviceRef.current = {
      device: usbDevice,
      endpointNumber: outInterface.alternate.endpoints.find(e => e.direction === 'out').endpointNumber
    };

    setConnected(true);
    console.log('[WebUSB] Interfaces:', usbDevice.configuration.interfaces.map(i => ({
      interfaceNumber: i.interfaceNumber,
      endpoints: i.alternate.endpoints
    })));
    console.log('[WebUSB] Device connected:', usbDevice.productName);

  } catch (err) {
    console.error('[WebUSB] Connection failed:', err);
    setConnected(false);
  }
};


const openTray = async () => {
  const isConnected = await connectIfNeeded();
  if (!isConnected || !deviceRef.current?.device) {
    console.error('[WebUSB] Cannot open tray: Not connected');
    return;
  }

  try {
    const { device, endpointNumber } = deviceRef.current;
    const data = new TextEncoder().encode(ESC_POS_PULSE_COMMAND);
    await device.transferOut(endpointNumber, data);
    console.log('[WebUSB] Tray opened on device:', device.productName);
  } catch (err) {
    console.error('[WebUSB] Failed to open tray:', err);
  }
};




  const connectWithRetry = async (retries = 3, delay = 1000) => {
    if (connecting.current) {
      console.log('[WebUSB] Connection attempt already in progress');
      return;
    }
    connecting.current = true;

    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const devices = await navigator.usb.getDevices();
        const targetDevice = devices.find((d) => d.vendorId === 0x04b8);

        if (targetDevice) {
          await connectToDevice(targetDevice);
          connecting.current = false;
          return true;
        } else {
          throw new Error('No matching USB device found');
        }
      } catch (err) {
        console.error(`[WebUSB] Retry ${attempt} failed:`, err);
        if (attempt === retries) {
          setConnected(false);
          connecting.current = false;
          return false;
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    connecting.current = false;
    return false;
  };

const connectIfNeeded = async () => {
  if (connected) return true;
  if (connecting.current) {
    console.log('[WebUSB] Waiting for ongoing connection...');
    while (connecting.current) await new Promise(res => setTimeout(res, 100));
    return connected;
  }
  return await connectWithRetry();
};


  const requestDeviceAndConnect = async () => {
    try {
      const device = await navigator.usb.requestDevice({
        filters: [{ vendorId: 0x04b8 }],
      });
      if (device) {
        await connectToDevice(device);
        return true;
      }
    } catch (err) {
      console.error('[WebUSB] User denied or no device selected:', err);
    }
    return false;
  };

  useEffect(() => {
    if (initialized.current) return;

    connectIfNeeded();
    initialized.current = true;

    return () => {
  if (deviceRef.current?.device) {
    const { device } = deviceRef.current;
    (async () => {
      try {
        if (device.opened) {
          try {
            // Try to release interface (typically 0 or 1)
            await device.releaseInterface(0);
          } catch (e) {
            console.warn('[WebUSB] Release interface failed (maybe not claimed):', e.message);
          }
          await device.close();
          console.log('[WebUSB] Device closed');
        }
      } catch (err) {
        console.error('[WebUSB] Disconnect failed:', err);
      }
      setConnected(false);
    })();
  }
};

  }, []);

  const listDevices = async () => {
    const isConnected = await connectIfNeeded();
    if (!isConnected) {
      console.error('[WebUSB] Cannot list devices: Not connected');
      return [];
    }

    try {
      const devices = await navigator.usb.getDevices();
      console.log('[WebUSB] Devices:', devices);
      return devices.map((d) => `${d.productName} (vendorId: ${d.vendorId})`);
    } catch (err) {
      console.error('[WebUSB] Device listing failed:', err);
      return [];
    }
  };


  return { openTray, listDevices, connected, requestDeviceAndConnect };
};

export { useTray };
